import { cn } from '@repo/ui'
import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react'
import type { ComponentPropsWithoutRef, ForwardedRef } from 'react'

export type InlineArtifactInputProps = ComponentPropsWithoutRef<'input'>

export const InlineArtifactInput = forwardRef<HTMLInputElement, InlineArtifactInputProps>(function InlineArtifactInput(
  { className, type = 'text', ...props },
  ref
) {
  return (
    <input
      ref={ref}
      type={type}
      data-canvas-edit-shortcuts="true"
      className={cn(
        'h-auto w-full min-w-0 rounded-sm border border-transparent bg-transparent px-1 py-0.5 text-foreground shadow-none transition-[border-color,box-shadow,background-color] outline-none placeholder:text-muted-foreground hover:border-border hover:bg-background/60 focus-visible:border-ring focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20',
        className
      )}
      {...props}
    />
  )
})

export type InlineArtifactTextareaProps = ComponentPropsWithoutRef<'textarea'> & {
  maxAutoHeight?: number
}

function assignRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') ref(value)
  else if (ref !== null) ref.current = value
}

export const InlineArtifactTextarea = forwardRef<HTMLTextAreaElement, InlineArtifactTextareaProps>(
  function InlineArtifactTextarea({ className, maxAutoHeight = 320, value, defaultValue, ...props }, forwardedRef) {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const setRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node
        assignRef(forwardedRef, node)
      },
      [forwardedRef]
    )

    const measure = useCallback(() => {
      const textarea = textareaRef.current
      if (textarea === null) return
      textarea.style.height = 'auto'
      const height = Math.min(textarea.scrollHeight, maxAutoHeight)
      textarea.style.height = `${height}px`
      textarea.style.overflowY = textarea.scrollHeight > maxAutoHeight ? 'auto' : 'hidden'
    }, [maxAutoHeight])

    useLayoutEffect(() => measure(), [defaultValue, measure, value])

    useLayoutEffect(() => {
      const textarea = textareaRef.current
      if (textarea === null || typeof ResizeObserver === 'undefined') return

      const observer = new ResizeObserver(measure)
      observer.observe(textarea.parentElement ?? textarea)
      return () => observer.disconnect()
    }, [measure])

    return (
      <textarea
        ref={setRef}
        value={value}
        defaultValue={defaultValue}
        rows={1}
        data-canvas-edit-shortcuts="true"
        className={cn(
          'block min-h-7 w-full max-w-full min-w-0 resize-none rounded-sm border border-transparent bg-transparent px-1 py-0.5 text-foreground shadow-none transition-[border-color,box-shadow,background-color] outline-none placeholder:text-muted-foreground hover:border-border hover:bg-background/60 focus-visible:border-ring focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20',
          className
        )}
        {...props}
      />
    )
  }
)
